import React, { forwardRef, Fragment } from 'react';
import range from 'lodash/range';
import { default as GridRow, Props as GridRowProps } from './_partials/GridRow';
import { GridBreakpoint } from './types';
import { getGridConfiguration } from './helpers';

export interface Props extends Pick<
  GridRowProps,
  'getDuperbrowseItemId' | 'getElementId' | 'renderElement'
> {
  elementCount: number;
  breakpoints?: GridBreakpoint[];
  className?: string;
  containerWidth?: number;
  screenWidth?: number;
  // use screen width for breakpoints or container width
  breakByScreenWidth?: boolean;
  contentStrip?: React.ReactNode;
  /** @deprecated */
  contentStripHeight?: number;
  contentStripIndex?: number;
  enableScrollOptim?: boolean;
  withShadowSpace?: boolean;
  maxRows?: number;
  spacing?: number;
  // spacing never applied to rows, so to avoid breaking prior uses of Grid, we use rowSpacing when needed so
  rowSpacing?: number;
  // Offset tracking - offset is the raw offset of the grid row
  // its up to you to convert it etc
  onOffsetReached?: (offset: number) => void;
  rowAlign?: 'left' | 'center' | 'right';
  // keep original element aspect ratio when true (scale width and height)
  // otherwise stretch width to fill the row but keep height the same
  preserveAspectRatio?: boolean;
  // Inject ad with Nth row
  adAfterNRow?: number;
  adRenderer?: (rowId: number) => React.ReactNode;
  // dont handle lazy loading rows internally and use the external context value
  lazyloadFromContext?: boolean;
}

const Grid = forwardRef<HTMLDivElement, Props>(
  (
    {
      breakpoints,
      className,
      containerWidth,
      screenWidth,
      breakByScreenWidth = true,
      contentStrip,
      contentStripIndex,
      elementCount,
      enableScrollOptim = true,
      maxRows = Number.POSITIVE_INFINITY,
      spacing,
      rowSpacing,
      withShadowSpace,
      onOffsetReached,
      getDuperbrowseItemId,
      getElementId,
      renderElement,
      rowAlign,
      preserveAspectRatio = true,
      adAfterNRow,
      adRenderer,
      lazyloadFromContext,
    },
    ref
  ) => {
    const hasContentStrip = contentStrip !== undefined && contentStripIndex !== undefined;

    const { elementWidth, elementHeight, elementSpacing, elementsPerRow } = getGridConfiguration({
      breakpoints,
      screenWidth,
      containerWidth,
      breakByScreenWidth,
      elementSpacing: spacing,
      preserveAspectRatio,
    });

    const expectedRows = Math.ceil(elementCount / elementsPerRow);
    const rowCount = Math.min(maxRows, expectedRows);

    const shouldRenderAdForRow = row => {
      return adAfterNRow && row > 0 && row % adAfterNRow === 0;
    };

    const wrapperStyle: React.CSSProperties = rowSpacing
      ? {
          display: 'flex',
          flexDirection: 'column',
          gap: rowSpacing,
        }
      : {};

    return (
      <div className={className} ref={ref} style={wrapperStyle}>
        {range(rowCount).map(i => (
          <Fragment key={`grid-row${i}`}>
            <GridRow
              key={`grid-row${i}`}
              rowIdx={i}
              contentStrip={
                hasContentStrip &&
                (contentStripIndex === i || (rowCount <= contentStripIndex && i === rowCount - 1))
                  ? contentStrip
                  : undefined
              }
              start={i * elementsPerRow}
              end={Math.min((i + 1) * elementsPerRow, elementCount)}
              elementWidth={elementWidth}
              elementHeight={elementHeight}
              elementSpacing={elementSpacing}
              scrollOptims={enableScrollOptim}
              withShadowSpace={withShadowSpace}
              getDuperbrowseItemId={getDuperbrowseItemId}
              getElementId={getElementId}
              renderElement={renderElement}
              onOffsetReached={onOffsetReached}
              rowAlign={rowAlign}
              lazyloadFromContext={lazyloadFromContext}
            />
            {shouldRenderAdForRow(i) && adRenderer && adRenderer(i)}
          </Fragment>
        ))}
      </div>
    );
  }
);
Grid.displayName = 'Grid';

export default Grid;
